import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

// FAB is rendered into document.body via portal — immune to parent overflow:hidden
export default function DraggableFab({ onToggle, isActive, label, children, storageKey = 'swamed_fab_pos' }) {
    const btnRef = useRef(null);
    const dragState = useRef(null);
    const didDrag = useRef(false);

    const [pos, setPos] = useState(() => {
        try {
            const s = JSON.parse(localStorage.getItem(storageKey));
            if (s && typeof s.x === 'number' && typeof s.y === 'number') return { x: s.x, y: s.y };
        } catch {}
        // Default: right side, below Leaflet toolbar
        return { x: window.innerWidth - 58, y: 170 };
    });

    const [dragging, setDragging] = useState(false);
    const posRef = useRef(pos);
    posRef.current = pos;

    const clamp = useCallback((x, y) => {
        const bw = 44, bh = 44;
        return {
            x: Math.max(8, Math.min(x, window.innerWidth - bw - 8)),
            y: Math.max(8, Math.min(y, window.innerHeight - bh - 8)),
        };
    }, []);

    const onPointerDown = useCallback((e) => {
        if (e.button !== undefined && e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        dragState.current = { startX: e.clientX, startY: e.clientY, origX: posRef.current.x, origY: posRef.current.y };
        didDrag.current = false;
        setDragging(true);
    }, []);

    const onPointerMove = useCallback((e) => {
        if (!dragState.current) return;
        const dx = e.clientX - dragState.current.startX;
        const dy = e.clientY - dragState.current.startY;
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) didDrag.current = true;
        if (!didDrag.current) return;
        const next = clamp(dragState.current.origX + dx, dragState.current.origY + dy);
        setPos(next);
    }, [clamp]);

    const onPointerUp = useCallback((e) => {
        if (!dragState.current) return;
        dragState.current = null;
        setDragging(false);
        const cur = posRef.current;
        try { localStorage.setItem(storageKey, JSON.stringify({ x: cur.x, y: cur.y })); } catch {}
    }, [storageKey]);

    const onClick = useCallback((e) => {
        e.stopPropagation();
        if (didDrag.current) { didDrag.current = false; return; }
        onToggle?.();
    }, [onToggle]);

    // Panel direction: if FAB is in lower 45% of screen → open upward
    const openUp = pos.y > window.innerHeight * 0.55;
    // Panel horizontal: if FAB is near right edge → open leftward (right-aligned)
    const nearRight = pos.x > window.innerWidth * 0.6;

    const panelStyle = {
        position: 'fixed',
        zIndex: 9998,
        ...(openUp ? { bottom: window.innerHeight - pos.y + 6 } : { top: pos.y + 50 }),
        ...(nearRight ? { right: window.innerWidth - pos.x - 44 } : { left: pos.x }),
        maxWidth: Math.min(260, window.innerWidth - 16),
    };

    const btnStyle = {
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        zIndex: 9999,
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
    };

    return ReactDOM.createPortal(
        <>
            <button
                ref={btnRef}
                className={`irr-fab-btn${isActive ? ' irr-fab-active' : ''}${dragging ? ' dragging' : ''}`}
                style={btnStyle}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onClick={onClick}
                title={label}
            >
                💧
                {isActive && <span className="irr-fab-dot" />}
            </button>

            {children && (
                <div className="irr-fab-panel-wrapper" style={panelStyle}>
                    <div className="irr-fab-panel">
                        {children}
                    </div>
                </div>
            )}
        </>,
        document.body
    );
}
